// Guided tours (driver.js). The help button in the rail (#help-tour-btn) plays the navigation
// tour, and pulses until someone has seen it once; any element with data-n-tour="name" plays
// that tour. Other scripts add tours through the globals the portal has always had:
//
//   window.DriverTours.register('assistant-agent', () => [{ element: '#Name', popover: { … } }]);
//   window.AppTours.start('assistant-agent');
//
// driver.js itself is loaded on the first start, so pages that never play a tour don't pay
// for it. Its popover is dressed in n-tour classes (styles/components/n-tour.css) and keeps
// the driver-popover-* classes the e2e suite reads.
import type { Config, Driver, DriveStep, PopoverDOM, State } from 'driver.js';
import { $, $$ } from './dom';

type StepFactory = () => DriveStep[];

const STORAGE_PREFIX = 'app-tour-';
const registry = new Map<string, StepFactory>([['navigation', navigationTour]]);
let active: Driver | null = null;

const storage = {
	done: (name: string) => {
		try {
			return localStorage.getItem(STORAGE_PREFIX + name) === 'done';
		} catch {
			return false;
		}
	},
	mark: (name: string) => {
		try {
			localStorage.setItem(STORAGE_PREFIX + name, 'done');
		} catch {
			/* private mode */
		}
	},
	clear: (name: string) => {
		try {
			localStorage.removeItem(STORAGE_PREFIX + name);
		} catch {
			/* private mode */
		}
	},
};

const css = (name: string) => getComputedStyle(document.documentElement).getPropertyValue(name).trim();

/** Brings a rail item into view: the rail scrolls sideways on a phone. */
function reveal(element: Element | undefined): void {
	element?.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'instant' });
}

// One per place in the rail (the ids in Services/Navigation/NavigationCatalog.cs). Some ids
// predate the places they now open: nav-agent-action is the Inbox, nav-admin is Settings.
const descriptions: Record<string, string> = {
	'nav-home': 'Where things stand today: what needs you, what your agents are doing, and what just happened.',
	'nav-agent-action':
		'Everything waiting for a person: approvals, connections that need signing in and failed syncs. See who decides and what goes out, then approve or decline, one at a time or several at once.',
	'nav-activity':
		'One log of what your agents and workflows did, for whom, and who said yes. Admin changes are here too.',
	'nav-agent-definition':
		'Your agents. Open one to build it, test it in a chat, deploy it to your website, Teams or Telegram, and see its runs. All starts lists everything that sets them to work: the doors people talk through, and what happens, like new mail or a time of day.',
	'nav-knowledge-base':
		'What your agents know: knowledge bases, their documents and the sources that keep them up to date.',
	'nav-connector':
		'The systems your agents can reach, grouped by kind, and which agents use each one. MCP servers, credentials and the n8n and Zapier workflows live here too.',
	'nav-usage-analytics': 'What your agents cost and what they are worth, down to individual usage records.',
	'nav-admin':
		'People, roles, approval rules, your client and security. Hover it to see the settings available to you.',
};

function navigationTour(): DriveStep[] {
	const steps: DriveStep[] = [
		{
			popover: {
				title: 'Welcome!',
				description:
					'Let’s take a quick tour of the navigation. It only takes a moment, and you can replay it any time from the <strong>?</strong> button at the bottom of the rail.',
			},
		},
		{
			element: '#sidebar',
			popover: {
				title: 'Navigation Sidebar',
				description:
					'Your main navigation: eight places. Hover an icon to see what is inside it; on a phone the rail sits along the top.',
				side: 'right',
				align: 'center',
			},
		},
	];

	const workspace = $('#rail-workspace');
	if (workspace) {
		const platform = workspace.hasAttribute('data-platform');
		steps.push({
			element: '#rail-workspace',
			popover: {
				title: platform ? 'All clients' : 'Your client',
				description: platform
					? 'Nuvio’s view across every client. Open it to see how each one is doing and to manage the clients.'
					: 'The client you are working for. Everything you see belongs to it.',
				side: 'right',
				align: 'center',
			},
			onHighlightStarted: reveal,
		});
	}

	if ($('#rail-search')) {
		steps.push({
			element: '#rail-search',
			popover: {
				title: 'Search',
				description:
					'Find any page, agent, flow, knowledge base or connection. Press Ctrl K (⌘K on a Mac) from anywhere.',
				side: 'right',
				align: 'center',
			},
			onHighlightStarted: reveal,
		});
	}

	// Follow the rendered rail order, which is already filtered by permission.
	for (const item of $$('#sidebar nav [id^="nav-"]')) {
		const label = $('span:last-child', item);
		const title = (label?.textContent ?? item.textContent ?? '').trim();
		steps.push({
			element: `#${item.id}`,
			popover: {
				title,
				description: descriptions[item.id] ?? 'Open this section from the navigation rail.',
				side: 'right',
				align: 'start',
			},
			onHighlightStarted: reveal,
		});
	}

	steps.push(
		{
			element: '#theme-toggle',
			popover: {
				title: 'Light & Dark Mode',
				description: 'Switch between light and dark. Your choice is remembered.',
				side: 'right',
				align: 'center',
			},
			onHighlightStarted: reveal,
		},
		{
			element: '#user-menu-trigger',
			popover: {
				title: 'Your Account',
				description: 'Your profile, your roles at a glance, and signing out.',
				side: 'right',
				align: 'end',
			},
			onHighlightStarted: reveal,
		},
	);

	if ($('#assistant-launch')) {
		steps.push({
			element: '#assistant-launch',
			popover: {
				title: 'Ask Nuvio',
				description:
					'Get help finding a page, understanding setup or choosing an agent kit. Review any proposed changes before confirming them.',
				side: 'left',
				align: 'end',
			},
		});
	}

	steps.push({
		element: '#help-tour-btn',
		popover: {
			title: 'You’re All Set!',
			description: 'Click this button any time to replay the tour. <em>Happy navigating!</em>',
			side: 'right',
			align: 'end',
		},
		onHighlightStarted: reveal,
	});
	return steps;
}

/** Dresses driver's popover in the design system: glass card, n-button footer, progress dots. */
function dress(popover: PopoverDOM, state: State, total: number): void {
	popover.wrapper.classList.add('n-tour');
	// driver rewrites the arrow's classes as it places it, so hide it directly: the stage is the pointer.
	popover.arrow.style.display = 'none';
	popover.title.classList.add('n-tour-title');
	popover.description.classList.add('n-tour-text');
	popover.footer.classList.add('n-tour-footer');
	popover.progress.classList.add('n-tour-progress');
	popover.footerButtons.classList.add('n-tour-buttons');
	popover.closeButton.classList.add('n-tour-close');
	popover.closeButton.setAttribute('aria-label', 'Close the tour');
	for (const [button, variant] of [
		[popover.previousButton, 'ghost'],
		[popover.nextButton, 'primary'],
	] as const) {
		button.classList.add('n-button');
		button.dataset.size = 'sm';
		button.dataset.variant = variant;
	}

	const current = state.activeIndex ?? 0;
	if (total < 2) return;
	if (total > 10) {
		popover.progress.textContent = `${current + 1} of ${total}`;
		return;
	}
	const dots = document.createElement('span');
	dots.className = 'n-tour-dots';
	dots.setAttribute('role', 'progressbar');
	dots.setAttribute('aria-label', 'Tour progress');
	dots.setAttribute('aria-valuemin', '1');
	dots.setAttribute('aria-valuemax', String(total));
	dots.setAttribute('aria-valuenow', String(current + 1));
	for (let i = 0; i < total; i++) {
		const dot = document.createElement('span');
		dot.className = 'n-tour-dot';
		if (i <= current) dot.dataset.state = i === current ? 'current' : 'done';
		dots.append(dot);
	}
	popover.progress.replaceChildren(dots);
}

function pulse(on: boolean): void {
	$('#help-tour-btn')?.classList.toggle('n-tour-pulse', on);
}

/** Plays a registered tour. Options are merged onto the base config. */
export async function startTour(name: string, options: Partial<Config> = {}): Promise<void> {
	const factory = registry.get(name);
	if (!factory) {
		console.warn(`[tours] unknown tour: ${name}`);
		return;
	}
	const returnFocus = document.activeElement as HTMLElement | null;
	active?.destroy();
	active = null;

	const steps = factory();
	if (!steps.length) return;
	$('#n-flyout')?.removeAttribute('data-open');
	const { driver } = await import('driver.js');
	const { onPopoverRender, onDestroyed, ...rest } = options;
	const dark = document.documentElement.classList.contains('dark');
	const config: Config = {
		steps,
		animate: true,
		smoothScroll: true,
		allowClose: true,
		allowKeyboardControl: true,
		// The ink token in light; plain black in dark, where ink is the light text colour.
		overlayColor: dark ? 'rgb(0 0 0)' : css('--n-ink') || 'rgb(15 23 42)',
		overlayOpacity: dark ? 0.6 : 0.32,
		stagePadding: 8,
		stageRadius: 16,
		popoverOffset: 14,
		showProgress: true,
		progressText: '{{current}} of {{total}}',
		nextBtnText: 'Next',
		prevBtnText: 'Back',
		doneBtnText: 'Done',
		...rest,
		onPopoverRender: (popover, opts) => {
			dress(popover, opts.state, opts.config.steps?.length ?? 0);
			onPopoverRender?.(popover, opts);
		},
		onDestroyed: (element, step, opts) => {
			storage.mark(name);
			pulse(false);
			active = null;
			onDestroyed?.(element, step, opts);
			// Let driver.js finish tearing down its focus trap first.
			requestAnimationFrame(() => {
				if (!active && returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
			});
		},
	};
	requestAnimationFrame(() => {
		active = driver(config);
		active.drive();
	});
}

declare global {
	interface Window {
		/** Plays tours: start(name), hasCompleted(name), reset(name), refresh(). */
		AppTours: {
			start: (name: string, options?: Partial<Config>) => Promise<void>;
			hasCompleted: (name: string) => boolean;
			reset: (name: string) => void;
			refresh: () => void;
		};
		/** The tour registry: register(name, () => steps), list(). */
		DriverTours: {
			register: (name: string, factory: StepFactory) => void;
			getSteps: (name: string) => DriveStep[];
			list: () => string[];
		};
	}
}

window.AppTours = {
	start: startTour,
	hasCompleted: storage.done,
	reset: storage.clear,
	refresh: () => active?.refresh(),
};
window.DriverTours = {
	register: (name, factory) => void registry.set(name, factory),
	getSteps: (name) => registry.get(name)?.() ?? [],
	list: () => [...registry.keys()],
};

export function initTours(): void {
	document.addEventListener('click', (e) => {
		const trigger = (e.target as Element).closest<HTMLElement>('#help-tour-btn, [data-n-tour]');
		if (!trigger) return;
		e.preventDefault();
		e.stopPropagation();
		void startTour(trigger.dataset.nTour ?? 'navigation');
	});
	if ($('#help-tour-btn') && !storage.done('navigation')) pulse(true);
}
